import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Link,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  CircularProgress,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
} from '@firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { useAuth } from '../service/AuthContext';
import { db, storage } from '../service/Firebase-config';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import DoneIcon from '@mui/icons-material/Done';
import PendingIcon from '@mui/icons-material/Pending';
import DeleteIcon from '@mui/icons-material/Delete';
import DialogEditHomework from '../form/DialogEditHomework';
import DialogNewHomework from '../form/DialogNewHomework';
import DialogNewJournal from '../form/DialogNewJournal';
import DialogEditRating from '../form/DialogEditRating';
import Title from './Title';
import TablePaginationActions from '../common/TablePagination';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';
import { getFileExtension } from '../common/util';

export default function HomeworkTablePerTeacher() {
  const { currentUser } = useAuth();
  const { selectedUser } = useGlobalContext();
  const { setOpenEditHw, setSelectedHw, setRating } = useHomeworkContext();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [homeworks, setHomeworks] = useState([]);
  const [data, setData] = useState([]);
  const [openNewHw, setOpenNewHw] = useState(false);
  const [openNewJournal, setOpenNewJournal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEditRating, setOpenEditRating] = useState(null);
  const [selectedDeleteHw, setSelectedDeleteHw] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openNewHomework = () => setOpenNewHw(true);
  const closeNewHomework = () => setOpenNewHw(false);

  const openEditHomework = async (hw) => {
    setSelectedHw(hw);
    setRating(hw.rating);
    setOpenEditHw(true);

    const teacherRef = doc(db, 'teachers', currentUser.uid);
    const docSnapshot = await getDoc(teacherRef);

    if (docSnapshot.exists()) {
      const newUpdate = docSnapshot.data().newUpdate || [];
      const newUpdates = newUpdate.filter((update) => update.homeworkId !== hw.id);
      await updateDoc(teacherRef, { newUpdate: newUpdates });
    }
  };

  const handleDeleteHomework = async () => {
    if (!selectedDeleteHw || !selectedUser?.uid) return;

    setIsDeleting(true);

    try {
      const studentRef = doc(db, 'students', selectedUser.uid);
      const teacherRef = doc(db, 'teachers', selectedDeleteHw.teacherUid);
      const homeworkRef = doc(
        db,
        `students/${selectedUser.uid}/homeworks/`,
        selectedDeleteHw.id
      );

      const studentFiles = selectedDeleteHw.fileStudent || [];
      const teacherFiles = selectedDeleteHw.fileTeacher || [];
      const allFiles = [...studentFiles, ...teacherFiles];

      // 1. storage 삭제
      await Promise.all(
        allFiles.map(async (file) => {
          if (!file?.savedName) return;

          const fileRef = ref(
            storage,
            `file/${selectedUser.uid}/${selectedDeleteHw.id}/${file.savedName}`
          );

          try {
            await deleteObject(fileRef);
          } catch (error) {
            console.error('Failed to delete storage file:', file.savedName, error);
          }
        })
      );

      // 2. student newUpdate 정리
      const studentSnap = await getDoc(studentRef);
      if (studentSnap.exists()) {
        const newUpdate = studentSnap.data().newUpdate || [];

        const filtered = newUpdate.filter(
          (item) => item?.homeworkId !== selectedDeleteHw.id
        );

        if (filtered.length !== newUpdate.length) {
          await updateDoc(studentRef, { newUpdate: filtered });
        }
      }

      // 3. teacher newUpdate 정리
      if (selectedDeleteHw.teacherUid) {
        const teacherSnap = await getDoc(teacherRef);

        if (teacherSnap.exists()) {
          const newUpdate = teacherSnap.data().newUpdate || [];

          const filtered = newUpdate.filter(
            (item) => item?.homeworkId !== selectedDeleteHw.id
          );

          if (filtered.length !== newUpdate.length) {
            await updateDoc(teacherRef, { newUpdate: filtered });
          }
        }
      }

      // 4. homework 삭제
      await deleteDoc(homeworkRef);

      setSelectedDeleteHw(null);
    } catch (error) {
      console.error('Failed to delete homework:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleQuery = (event) => {
    const queryText = event.target.value.toLowerCase();
    setData(
      homeworks.filter(
        (row) => !queryText || (row.homework || '').toLowerCase().includes(queryText)
      )
    );
  };

  useEffect(() => {
    if (selectedUser) {
      const collectionRef = collection(db, `students/${selectedUser.uid}/homeworks`);
      const orderedQuery = query(
        collectionRef,
        where('teacherUid', '==', currentUser.uid),
        orderBy('createdDate', 'desc')
      );

      const unsubscribe = onSnapshot(orderedQuery, (snapshot) => {
        const newHomeworks = snapshot.docs.map((docItem) => ({
          ...docItem.data(),
          id: docItem.id,
        }));
        setHomeworks(newHomeworks);
        setData(newHomeworks);
        setLoading(false);
      });

      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [selectedUser, currentUser.uid]);

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Title>Homework List</Title>
          <TextField
            id="input-with-sx"
            label="Search Homework"
            size="small"
            variant="outlined"
            onChange={handleQuery}
            sx={{ marginLeft: 2 }}
          />
        </Box>

        <Button
          sx={{ display: selectedUser ? undefined : 'none' }}
          variant="contained"
          onClick={openNewHomework}
        >
          New Homework
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small" component={Paper}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Homework</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Rating</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Journal</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>File</TableCell>
              <TableCell align="center" sx={{ width: 56 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {(rowsPerPage > 0
              ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : data
            ).map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    cursor: 'pointer',
                  },
                }}
              >
                <TableCell onClick={() => openEditHomework(row)}>
                  {new Date(row.createdDate.seconds * 1000).toLocaleString('en-US', {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </TableCell>

                <Tooltip title={row.homework || ''}>
                  <TableCell onClick={() => openEditHomework(row)}>
                    {row.homework?.length < 40
                      ? row.homework
                      : `${row.homework.substring(0, 40)}...`}
                  </TableCell>
                </Tooltip>

                <TableCell onClick={() => openEditHomework(row)}>
                  {row.subject}
                </TableCell>

                <TableCell onClick={() => setOpenEditRating(row)}>
                  <Rating
                    readOnly
                    name="simple-controlled"
                    value={row?.rating || 0}
                    size="small"
                  />
                </TableCell>

                <TableCell onClick={() => setOpenNewJournal(row)}>
                  <Tooltip title={row.journal || 'journal pending'} placement="top-end">
                    <IconButton>
                      {row.journal ? <DoneIcon /> : <PendingIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  {row.fileStudent &&
                    row.fileStudent.map((file) => (
                      <Tooltip key={file.savedName || file.fileName} title={file.fileName}>
                        <Link variant="caption" href={file.fileUrl} target="_blank">
                          <Box component="span" mx={0.5}>
                            {getFileExtension(file.fileName) === 'pdf' ? (
                              <PictureAsPdfOutlinedIcon color="warning" />
                            ) : (
                              <FolderZipOutlinedIcon color="info" />
                            )}
                          </Box>
                        </Link>
                      </Tooltip>
                    ))}

                  {row.fileTeacher &&
                    row.fileTeacher.map((file) => (
                      <Tooltip
                        key={`teacher-${file.savedName || file.fileName}`}
                        title={file.fileName}
                      >
                        <Link variant="caption" href={file.fileUrl} target="_blank">
                          <Box component="span" mx={0.5}>
                            {getFileExtension(file.fileName) === 'pdf' ? (
                              <PictureAsPdfOutlinedIcon color="warning" />
                            ) : (
                              <FolderZipOutlinedIcon color="info" />
                            )}
                          </Box>
                        </Link>
                      </Tooltip>
                    ))}
                </TableCell>

                <TableCell
                  align="center"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <IconButton onClick={() => setSelectedDeleteHw(row)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={7} />
              </TableRow>
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                colSpan={7}
              />
            </TableRow>
          </TableFooter>
        </Table>
      )}

      <Dialog
        open={Boolean(selectedDeleteHw)}
        onClose={() => {
          if (!isDeleting) setSelectedDeleteHw(null);
        }}
      >
        <DialogTitle>Delete Homework</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this homework and all related files?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelectedDeleteHw(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteHomework}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogNewHomework open={openNewHw} closeNewHomework={closeNewHomework} />
      <DialogEditHomework />
      <DialogEditRating
        open={!!openEditRating}
        handleClose={() => setOpenEditRating(null)}
        row={openEditRating}
      />
      <DialogNewJournal
        open={!!openNewJournal}
        closeJournal={() => setOpenNewJournal(null)}
        row={openNewJournal}
      />
    </React.Fragment>
  );
}